<template>
  <article class="position-fair-pay-card">
    <ExBreadcrumbs :breadcrumbsList="breadcrumbs" @onClickBreadcrumb="onBreadcrumbClick" />

    <header class="position-fair-pay-card__header">
      <div class="position-fair-pay-card__identity">
        <h1 class="position-fair-pay-card__title">{{ title }}</h1>
        <p class="position-fair-pay-card__subtitle">{{ subtitle }}</p>
      </div>
      <span class="position-fair-pay-card__status">{{ statusLabel }}</span>
    </header>

    <div
      v-if="loading"
      class="position-fair-pay-card__loading"
      role="status"
      aria-live="polite"
      data-testid="position-fair-pay-card-skeleton"
    >
      <div class="position-fair-pay-card__skeleton-lane">
        <span class="position-fair-pay-card__skeleton-label" />
        <span class="position-fair-pay-card__skeleton-bar" />
      </div>
      <div class="position-fair-pay-card__skeleton-lane">
        <span class="position-fair-pay-card__skeleton-label" />
        <span
          class="position-fair-pay-card__skeleton-bar position-fair-pay-card__skeleton-bar--short"
        />
      </div>
      <div class="position-fair-pay-card__skeleton-lane">
        <span class="position-fair-pay-card__skeleton-label" />
        <span
          class="position-fair-pay-card__skeleton-bar position-fair-pay-card__skeleton-bar--medium"
        />
      </div>
      <div class="position-fair-pay-card__skeleton-history">
        <span
          v-for="n in 2"
          :key="`history-skeleton-${n}`"
          class="position-fair-pay-card__skeleton-history-row"
        />
      </div>
    </div>

    <div v-else-if="error" class="position-fair-pay-card__error" role="alert">
      <EXErrorState title="Failed to load position" :message="errorText" @retry="$emit('retry')" />
    </div>

    <div v-else-if="isEmpty" class="position-fair-pay-card__empty">
      <EXEmptyState
        title="No pay range subscribed"
        supporting-text="This position hasn’t had any pay range assigned."
        title-class="position-fair-pay-card__empty-title"
        supporting-text-class="position-fair-pay-card__empty-copy"
      />
    </div>

    <template v-else>
      <div class="position-fair-pay-card__timeline" role="img" :aria-label="timelineAriaLabel">
        <p class="position-fair-pay-card__track-label position-fair-pay-card__track-label--subscriptions">
          Subscriptions
        </p>
        <p class="position-fair-pay-card__track-label position-fair-pay-card__track-label--versions">
          Versions
        </p>
        <p class="position-fair-pay-card__track-label position-fair-pay-card__track-label--occupied">
          Occupied by
        </p>

        <div class="position-fair-pay-card__canvas">
          <p
            class="position-fair-pay-card__today-label"
            :style="{ left: `${todayPct}%` }"
          >
            today
          </p>

          <PayRangeSubscriptionBar
            v-for="bar in subscriptionBars"
            :key="bar.key"
            class="position-fair-pay-card__sub-bar"
            :style="bar.style"
            :state="bar.state"
            :subscription-code="bar.code"
            :title="bar.title"
          />
          <PayRangeVersionBar
            v-for="bar in versionBars"
            :key="bar.key"
            class="position-fair-pay-card__version-bar"
            :style="bar.style"
            :state="bar.state"
            :version-number="bar.versionNumber"
            :amount-min="bar.amountMin"
            :amount-max="bar.amountMax"
          />
          <div
            v-for="bar in gapBars"
            :key="bar.key"
            class="position-fair-pay-card__gap-bar"
            :style="bar.style"
            role="img"
            :aria-label="bar.ariaLabel"
          >
            <span
              class="position-fair-pay-card__gap-bar-fill"
              aria-hidden="true"
            />
            <span
              class="position-fair-pay-card__gap-bar-label"
              :class="{
                'position-fair-pay-card__gap-bar-label--overflow': bar.labelOverflow,
              }"
              :style="bar.labelStyle"
            >
              {{ bar.label }}
            </span>
          </div>
          <EmployeeToPrimaryAssignmentBar
            v-for="bar in assignmentBars"
            :key="bar.key"
            class="position-fair-pay-card__assignment-bar"
            :style="bar.style"
            :first-name="bar.firstName"
            :last-name="bar.lastName"
            :avatar="bar.avatar"
            :fte="bar.fte"
          />

          <div
            class="position-fair-pay-card__today-line"
            :style="{ left: `${todayPct}%` }"
          />
          <div class="position-fair-pay-card__axis" />
          <span
            v-for="label in yearMarks"
            :key="label.year"
            class="position-fair-pay-card__year"
            :style="{ left: `${label.left}%` }"
          >
            {{ label.year }}
          </span>
        </div>
      </div>

      <div class="position-fair-pay-card__rule" />

      <div class="position-fair-pay-card__legend">
        <span class="position-fair-pay-card__legend-title">Subscription:</span>
        <span
          v-for="item in legend"
          :key="item.state"
          class="position-fair-pay-card__legend-item"
        >
          <span
            class="position-fair-pay-card__swatch"
            :class="`position-fair-pay-card__swatch--${item.state}`"
          />
          {{ item.label }}
        </span>
      </div>

      <h2 class="position-fair-pay-card__history-title">Subscription history</h2>
      <div class="position-fair-pay-card__history">
        <PositionSubscriptionHistoryRow
          v-for="row in historyRows"
          :key="row.id"
          :subscription-id="row.id"
          :subscription-code="row.code"
          :position-title="row.title"
          :effective-window="row.effectiveWindow"
          :versions="row.versions"
          :coverage-gaps="row.coverageGaps"
          :today="resolvedToday"
          @unsubscribe="onUnsubscribe"
        />
      </div>
    </template>
  </article>
</template>

<script setup>
import { computed } from "vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import EXEmptyState from "@components/EXEmptyState/EXEmptyState.vue";
import EXErrorState from "@components/EXErrorState/EXErrorState.vue";
import { FAIR_PAY_TAB, fairPayListRoute } from "@components/FairPayMenu/fairPayMenuConstants";
import PayRangeSubscriptionBar from "@components/PayRangeSubscriptionBar/PayRangeSubscriptionBar.vue";
import PayRangeVersionBar from "@components/PayRangeVersionBar/PayRangeVersionBar.vue";
import EmployeeToPrimaryAssignmentBar from "@components/EmployeeToPrimaryAssignmentBar/EmployeeToPrimaryAssignmentBar.vue";
import PositionSubscriptionHistoryRow from "@components/PositionSubscriptionHistoryRow/PositionSubscriptionHistoryRow.vue";
import { deriveSubscriptionState } from "@components/PayRangeSubscriptionCard/payRangeSubscriptionCardUtils";
import {
  buildAxisRange,
  clipToParent,
  clipVersionWindow,
  collectWindows,
  formatCodeSubtitle,
  formatStatusLabel,
  gapBarLabelText,
  gapLabelOverflowsSpan,
  layoutVersionWindow,
  layoutWindow,
  legendStates,
  sortSubscriptionsNewestFirst,
  todayLeft,
  toTimelineBarState,
  yearLabels,
} from "./positionFairPayCardUtils";

const props = defineProps({
  title: {
    type: String,
    default: "",
  },
  code: {
    type: String,
    default: "",
  },
  status: {
    type: String,
    default: "active",
  },
  today: {
    type: [String, Date],
    default: null,
  },
  subscriptions: {
    type: Array,
    default: () => [],
  },
  assignments: {
    type: Array,
    default: () => [],
  },
  asOf: {
    type: String,
    default: "",
  },
  /** ER-12850 — table-level states owned by the consuming view's fetch. */
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: [Boolean, String],
    default: false,
  },
});

const emit = defineEmits(["back", "unsubscribe", "retry"]);

const breadcrumbs = computed(() => [
  {
    text: "Positions",
    route: fairPayListRoute(
      FAIR_PAY_TAB.POSITION_PAY_RANGES,
      props.asOf ? { asOf: props.asOf } : {}
    ),
  },
  { text: "Position Fair Pay", disabled: true },
]);

const resolvedToday = computed(() => props.today || new Date());
const isEmpty = computed(() => !props.subscriptions.length);
const errorText = computed(() =>
  typeof props.error === "string" && props.error
    ? props.error
    : "Something went wrong while loading this position. Please try again."
);
const subtitle = computed(() => formatCodeSubtitle(props.code));
const statusLabel = computed(() => formatStatusLabel(props.status));

const axis = computed(() =>
  buildAxisRange(collectWindows(props.subscriptions, props.assignments), resolvedToday.value)
);

const todayPct = computed(() => todayLeft(axis.value));
const yearMarks = computed(() => yearLabels(axis.value));
const legend = computed(() => legendStates(props.subscriptions, resolvedToday.value));

const historyRows = computed(() =>
  sortSubscriptionsNewestFirst(props.subscriptions).map((sub) => ({
    id: sub.id,
    code: sub.code,
    title: sub.title,
    effectiveWindow: { start: sub.start, end: sub.end },
    versions: (sub.versions || []).flatMap((version) => {
      const clipped = clipVersionWindow(version, sub);
      if (!clipped) return [];
      return [
        {
          ordinal: version.ordinal,
          effectiveWindow: { start: clipped.start, end: clipped.end },
          payBandMin: version.payBandMin,
          payBandMax: version.payBandMax,
        },
      ];
    }),
    coverageGaps: (sub.coverageGaps || []).map((gap) => ({
      from: gap.from,
      to: gap.to,
    })),
  }))
);

const subscriptionBars = computed(() =>
  props.subscriptions.map((sub) => {
    const box = layoutWindow(sub.start, sub.end, axis.value);
    return {
      key: `sub-${sub.id}`,
      code: sub.code,
      title: sub.title,
      state: toTimelineBarState(deriveSubscriptionState(sub.start, sub.end, resolvedToday.value)),
      style: { left: `${box.left}%`, width: `${box.width}%`, top: "22px" },
    };
  })
);

const versionBars = computed(() =>
  props.subscriptions.flatMap((sub) =>
    (sub.versions || []).flatMap((version, index) => {
      const clipped = clipVersionWindow(version, sub);
      if (!clipped) return [];
      const box = layoutVersionWindow(clipped.start, clipped.end, sub.start, sub.end, axis.value);
      return [
        {
          key: `ver-${sub.id}-${version.ordinal}-${index}`,
          versionNumber: version.ordinal,
          amountMin: version.payBandMin,
          amountMax: version.payBandMax,
          state: toTimelineBarState(
            deriveSubscriptionState(clipped.start, clipped.end, resolvedToday.value)
          ),
          style: { left: `${box.left}%`, width: `${box.width}%`, top: "66px" },
        },
      ];
    })
  )
);

const gapBars = computed(() => {
  const label = gapBarLabelText();
  return props.subscriptions.flatMap((sub) =>
    (sub.coverageGaps || []).flatMap((gap, index) => {
      const clipped = clipToParent(gap.from, gap.to, sub.start, sub.end);
      if (!clipped) return [];
      const box = layoutWindow(clipped.start, clipped.end, axis.value);
      const labelOverflow = gapLabelOverflowsSpan(box.width);
      return [
        {
          key: `gap-${sub.id}-${index}-${gap.from}`,
          label,
          labelOverflow,
          ariaLabel: `${label} from ${gap.from}${gap.to ? ` to ${gap.to}` : " onward"}`,
          labelStyle: labelOverflow ? { left: "calc(100% + 8px)" } : {},
          style: { left: `${box.left}%`, width: `${box.width}%`, top: "66px" },
        },
      ];
    })
  );
});

const assignmentBars = computed(() =>
  props.assignments.map((assignment) => {
    const box = layoutWindow(assignment.start, assignment.end, axis.value);
    return {
      key: `asg-${assignment.id}`,
      firstName: assignment.firstName,
      lastName: assignment.lastName,
      avatar: assignment.avatar,
      fte: assignment.fte,
      style: { left: `${box.left}%`, width: `${box.width}%`, top: "100px" },
    };
  })
);

const timelineAriaLabel = computed(() =>
  `Pay range timeline for ${props.title || "this position"}`
);

function onBreadcrumbClick(breadcrumb) {
  if (breadcrumb?.disabled || !breadcrumb?.route) return;
  emit("back", breadcrumb.route);
}

function onUnsubscribe(subscriptionId) {
  emit("unsubscribe", subscriptionId);
}
</script>

<style scoped>
.position-fair-pay-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  width: 100%;
  padding: 24px;
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
}

.position-fair-pay-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 40px;
}

.position-fair-pay-card__identity {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.position-fair-pay-card__title,
.position-fair-pay-card__subtitle,
.position-fair-pay-card__empty-title,
.position-fair-pay-card__empty-copy,
.position-fair-pay-card__track-label,
.position-fair-pay-card__today-label,
.position-fair-pay-card__year,
.position-fair-pay-card__legend,
.position-fair-pay-card__history-title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-style: normal;
  line-height: normal;
}

.position-fair-pay-card__title {
  font-size: 18px;
  font-weight: 600;
  color: #101828;
}

.position-fair-pay-card__subtitle {
  font-size: 13px;
  font-weight: 400;
  color: #667085;
}

.position-fair-pay-card__status {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 8px;
  background: #e8fbf0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  color: #027a48;
}

.position-fair-pay-card__empty {
  border-top: 1px solid #eaecf0;
}

.position-fair-pay-card__loading {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
  border-top: 1px solid #eaecf0;
}

.position-fair-pay-card__skeleton-lane {
  display: flex;
  align-items: center;
  gap: 16px;
}

.position-fair-pay-card__skeleton-label {
  flex: 0 0 120px;
  height: 12px;
  border-radius: 6px;
  background: #f2f4f7;
  animation: position-fair-pay-card-pulse 1.4s ease-in-out infinite;
}

.position-fair-pay-card__skeleton-bar {
  flex: 1 1 0;
  height: 24px;
  border-radius: 6px;
  background: #f2f4f7;
  animation: position-fair-pay-card-pulse 1.4s ease-in-out infinite;
}

.position-fair-pay-card__skeleton-bar--short {
  flex: 0 0 32%;
}

.position-fair-pay-card__skeleton-bar--medium {
  flex: 0 0 56%;
}

.position-fair-pay-card__skeleton-history {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
}

.position-fair-pay-card__skeleton-history-row {
  height: 40px;
  border-radius: 8px;
  background: #f2f4f7;
  animation: position-fair-pay-card-pulse 1.4s ease-in-out infinite;
}

.position-fair-pay-card__error {
  border-top: 1px solid #eaecf0;
}

@keyframes position-fair-pay-card-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

.position-fair-pay-card__timeline {
  position: relative;
  width: 100%;
  height: 190px;
  overflow: visible;
}

.position-fair-pay-card__track-label {
  position: absolute;
  left: 0;
  width: 130px;
  font-size: 13px;
  font-weight: 400;
  color: #667085;
}

.position-fair-pay-card__track-label--subscriptions {
  top: 34px;
}

.position-fair-pay-card__track-label--versions {
  top: 72px;
}

.position-fair-pay-card__track-label--occupied {
  top: 110px;
}

.position-fair-pay-card__canvas {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 140px;
}

.position-fair-pay-card__today-label {
  position: absolute;
  top: 2px;
  z-index: 3;
  transform: translateX(-50%);
  font-size: 11px;
  font-weight: 400;
  color: #99a1af;
  white-space: nowrap;
}

.position-fair-pay-card__sub-bar,
.position-fair-pay-card__version-bar,
.position-fair-pay-card__assignment-bar,
.position-fair-pay-card__gap-bar {
  position: absolute;
  z-index: 1;
}

.position-fair-pay-card__gap-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 26px;
  border-radius: 6px;
  overflow: visible;
}

.position-fair-pay-card__gap-bar-fill {
  position: absolute;
  inset: 0;
  border-radius: 6px;
  background: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 6px,
    #fef3f2 6px,
    #fef3f2 12px
  );
}

.position-fair-pay-card__gap-bar-label {
  position: relative;
  z-index: 1;
  padding: 0 8px;
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: normal;
  color: #b42318;
  white-space: nowrap;
}

.position-fair-pay-card__gap-bar-label--overflow {
  position: absolute;
  top: 50%;
  z-index: 2;
  padding: 0;
  color: #344054;
  transform: translateY(-50%);
}

.position-fair-pay-card__version-bar {
  height: 26px;
}

.position-fair-pay-card__version-bar :deep(.pay-range-version-bar) {
  width: 100%;
}

.position-fair-pay-card__today-line {
  position: absolute;
  top: 18px;
  bottom: 30px;
  z-index: 2;
  width: 1.5px;
  height: auto;
  background: #99a1af;
}

.position-fair-pay-card__axis {
  position: absolute;
  top: 160px;
  left: 0;
  width: 100%;
  height: 1px;
  background: #d0d5dd;
}

.position-fair-pay-card__year {
  position: absolute;
  top: 166px;
  font-size: 11px;
  font-weight: 400;
  color: #667085;
  white-space: nowrap;
}

.position-fair-pay-card__rule {
  width: 100%;
  height: 1px;
  background: #eaecf0;
}

.position-fair-pay-card__legend {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 11px;
  font-weight: 400;
  color: #667085;
}

.position-fair-pay-card__legend-title {
  flex-shrink: 0;
}

.position-fair-pay-card__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.position-fair-pay-card__swatch {
  box-sizing: border-box;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  border-style: solid;
}

.position-fair-pay-card__swatch--lapsed {
  background: #e3e7ec;
  border-width: 1.5px;
  border-color: #9ea4b0;
}

.position-fair-pay-card__swatch--current {
  background: #e8fbf0;
  border-width: 1.5px;
  border-color: #027a48;
}

.position-fair-pay-card__swatch--future {
  background: #f0f9ff;
  border-width: 1px;
  border-color: #eaecf0;
}

.position-fair-pay-card__history-title {
  font-size: 14px;
  font-weight: 600;
  color: #101828;
}

.position-fair-pay-card__history {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}
</style>
